/* eslint-disable @stencil-community/required-jsdoc */
import { Component, Host, h, Prop, State, Listen, Event, EventEmitter } from '@stencil/core';
import { Memoize, isEmpty, parseIfString } from '@springbokagency/stencil-base';
import { Splide } from '@splidejs/splide';
import { URLHash } from '@splidejs/splide-extension-url-hash';

import type { ModalVideoGroupItem } from '../video-modal/video-modal.types';

@Component({
  tag: 'efb-video-modal-slider',
  styleUrl: 'video-modal-slider.scss',
  shadow: true,
})
export class EfbVideoModalSlider {
  private sliderElement!: HTMLElement;

  private videoTitleRef!: HTMLDivElement;

  private slider: Splide;

  private titlePositionTimer: ReturnType<typeof setTimeout>;

  /**
   * State
   */

  @State() sliderActiveItem: number;

  /**
   * Props
   */

  @Prop() sliderContent: ModalVideoGroupItem | string;

  @Memoize('sliderContent')
  get parsedSliderContent(): ModalVideoGroupItem {
    return parseIfString(this.sliderContent);
  }

  @Prop() activeVideoIndex?: number;

  @Prop() muteVideos?: boolean;

  @Prop() playVideo?: boolean;

  @Prop() seekTo?: number;

  @Prop() videoTitle?: string;

  /**
   * Events
   */

  @Event() videoSliderSlideWillMove: EventEmitter<boolean>;

  @Event() videoSliderSlideChanged: EventEmitter<{ index: number }>;

  @Listen('videoEnded')
  videoEndedHandler() {
    if (this.activeVideoIndex + 1 < this.slider.length) {
      this.slider.go(this.activeVideoIndex + 1);
    }
  }

  /**
   * Events
   */

  private defineVideoTitlePosition(index: number) {
    this.sliderElement.style.setProperty('--video-title-position', `${index}`);

    this.titlePositionTimer = setTimeout(() => {
      // hide video title
      this.videoTitleRef.classList.add('is-hidden');
    }, 3000);
  }

  /**
   * Lifecycle Hooks
   */

  componentDidLoad() {
    this.slider = new Splide(this.sliderElement, {
      type: 'slider',
      perPage: 1,
      pagination: false,

      breakpoints: {
        640: {
          direction: 'ttb',
          height: '100%',
          arrows: false,
          pagination: true,
          paginationDirection: 'ttb',
        },
      },
    }).mount({ URLHash });

    // go to specific slide (useful for sharing feature)
    this.slider.go(this.activeVideoIndex);
    this.sliderActiveItem = this.activeVideoIndex || 0;
    // define the position of the video title
    this.defineVideoTitlePosition(this.activeVideoIndex);

    this.slider.on('active', (newIndex) => {
      this.sliderActiveItem = newIndex.index;

      // useful for page tracking
      this.videoSliderSlideChanged.emit({ index: newIndex.index });

      // define the position of the video title
      this.defineVideoTitlePosition(this.sliderActiveItem);

      // show video title
      this.videoTitleRef.classList.remove('is-hidden');
    });

    this.slider.on('move', () => {
      // useful to hide external link button before slide change
      this.videoSliderSlideWillMove.emit(true);

      // hide video title
      this.videoTitleRef.classList.add('is-hidden');

      // clear timer
      clearTimeout(this.titlePositionTimer);
    });
  }

  /**
   * Render
   */

  render() {
    return (
      <Host>
        <section
          class="video-modal-slider splide"
          aria-label="Video Slider"
          ref={(el) => {
            this.sliderElement = el as HTMLElement;
          }}
        >
          {/* Arrows */}
          <div class="splide__arrows">
            <button class="splide__arrow splide__arrow--prev">
              <efb-icon name="back-arrow" />
            </button>
            <button class="splide__arrow splide__arrow--next">
              <efb-icon name="forward-arrow" />
            </button>
          </div>
          {/* Slides */}
          {!isEmpty(this.parsedSliderContent?.videos) && (
            <div class="splide__track">
              <ul class="splide__list">
                {this.parsedSliderContent.videos.map((slide, index) => (
                  <li
                    class="splide__slide"
                    data-splide-hash={`vgroup=${this.parsedSliderContent.modalGroupId}&vid=${slide.id}`}
                  >
                    <efb-video-modal-item
                      playVideo={this.playVideo}
                      seekTo={this.seekTo}
                      videoIsMuted={this.muteVideos}
                      trackingId={slide.id}
                      index={`${index + 1}`}
                      vimeoId={slide.vimeoId}
                      isActiveItem={index === this.sliderActiveItem}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div class="pagination-wrapper">
            {/* Pagination */}
            <ul class="coucou splide__pagination"></ul>

            {/* Video title */}
            <div
              ref={(el) => {
                this.videoTitleRef = el;
              }}
              class="video-title"
            >
              {this.videoTitle}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
